import { PlusOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { useGetModuleOptionsQuery } from '../../../api/queries/getModuleOptions';
import { ModuleFilteringRule, ModuleType } from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useExcelUploadModal } from '../../../components/vehicleImportExport';
import { ImportExportType, useModuleSelectModal } from '../../../components/vehicleImportExport/ModuleSelectModal';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { DownloadType, exportVehicles } from '../../../utilities/export';
import hasPermissions from '../../../utilities/hasPermissions';
import useGoTo from '../../../utilities/useGoTo';
import MakeList from './MakeList';

const MakeListPage = () => {
    const { t } = useTranslation('makeList');

    const { token } = useAccountContext();
    const company = useCompany(true);
    const { id: companyId = null, displayName, countryCode } = company || {};

    const excelUploadModal = useExcelUploadModal(DownloadType.Makes);
    const moduleSelectUploadModal = useModuleSelectModal(DownloadType.Makes);

    const { data, loading } = useGetModuleOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { kind: ModuleType.SimpleVehicleManagement, companyId },
    });

    const goToNewMakePage = useGoTo('/admin/vehicles/makes/add');

    const showUploadExcelModal = useCallback(() => {
        if (!company) {
            message.warn('Please select a company before uploading.');
        } else if (data?.modules?.length > 1) {
            moduleSelectUploadModal.open(ImportExportType.Import);
        } else if (data?.modules?.length > 0) {
            excelUploadModal.open(data?.modules[0].id);
        }
    }, [company, excelUploadModal, moduleSelectUploadModal, data?.modules]);

    const downloadExcel = () => {
        if (!company) {
            message.warn('Please select a company before downloading.');
        } else if (data?.modules?.length > 1) {
            moduleSelectUploadModal.open(ImportExportType.Export);
        } else if (data?.modules?.length > 0) {
            exportVehicles(displayName, countryCode, DownloadType.Makes, false, data?.modules[0].id, token);
        }
    };

    const hasPermissionsToCreate = useMemo(() => {
        if (data?.modules?.length) {
            return data.modules.some(
                module =>
                    hasPermissions(module.permissions, [permissionKind.createVehicle]) ||
                    hasPermissions(module.permissions, [permissionKind.updateVehicle])
            );
        }

        return false;
    }, [data?.modules]);

    const hasPermissionsToView = useMemo(() => {
        if (data?.modules?.length) {
            return data.modules.some(
                module =>
                    hasPermissions(module.permissions, [permissionKind.viewVehicles]) ||
                    hasPermissions(module.permissions, [permissionKind.updateVehicle]) ||
                    hasPermissions(module.permissions, [permissionKind.createVehicle])
            );
        }

        return false;
    }, [data?.modules]);

    if (!data?.modules || loading) {
        return null;
    }

    const extra = (
        <>
            {hasPermissionsToCreate && (
                <Button icon={<PlusOutlined />} onClick={goToNewMakePage} type="primary">
                    {t('makeList:actions.addMake')}
                </Button>
            )}
            {company?.displayName !== 'BMW' && (
                <>
                    {hasPermissionsToCreate && (
                        <Button icon={<UploadOutlined />} onClick={showUploadExcelModal} type="primary">
                            {t('makeList:actions.importMakes')}
                        </Button>
                    )}
                    {hasPermissionsToView && (
                        <Button icon={<DownloadOutlined />} onClick={downloadExcel} type="primary">
                            {t('makeList:actions.exportMakes')}
                        </Button>
                    )}
                </>
            )}
        </>
    );

    return (
        <ConsolePageWithHeader extra={extra} title={t('makeList:title')}>
            <MakeList />
            {excelUploadModal.render()}
            {moduleSelectUploadModal.render()}
        </ConsolePageWithHeader>
    );
};

export default MakeListPage;
